import { FC, useCallback, useState } from "react";
import {
    useOrganizationAccessToken,
    useOrganizationWebhookPortalMutation,
    useRotateOrganizationAccessTokenMutation,
} from "@api/organizationAccessToken.api";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { Card } from "@design-system/Card/Card";
import { CardSection } from "@design-system/Card/CardSection";
import { HStack } from "@design-system/HStack/HStack";
import { PageSection } from "@design-system/PageSection/PageSection";
import { VStack } from "@design-system/VStack/VStack";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { CreatedTokenModal } from "@design-system/CreatedTokenModal/CreatedTokenModal";
import { formatTokenDate } from "@helpers/date";
import { formatTokenPrefix } from "@helpers/tokenPrefix";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { useCanManageOrganizationApi } from "@hooks/permissions/organization/useCanManageOrganizationApi";
import styles from "./ApiPage.module.scss";

export const ApiPage: FC = () => {
    const organization = useActiveOrganization();
    const canManageApi = useCanManageOrganizationApi();
    const organizationId = organization?.id ?? "";

    const {
        data: token,
        isLoading,
        isError,
        refetch,
    } = useOrganizationAccessToken(organizationId);
    const rotateMutation =
        useRotateOrganizationAccessTokenMutation(organizationId);
    const webhookPortalMutation =
        useOrganizationWebhookPortalMutation(organizationId);
    const { error: toastError } = useToaster();

    const [isConfirmingRotation, setIsConfirmingRotation] = useState(false);
    const [plaintext, setPlaintext] = useState<string | null>(null);

    const rotate = useCallback(async () => {
        try {
            const result = await rotateMutation.mutateAsync();
            setIsConfirmingRotation(false);
            setPlaintext(result.plaintext);
        } catch {
            toastError("Failed to rotate token");
        }
    }, [rotateMutation, toastError]);

    // Creating the very first token is not destructive, so it skips the
    // confirmation step that rotation needs.
    const handleTokenAction = useCallback(() => {
        // A failed load leaves us unable to tell whether a token already
        // exists, so we must never fall through to the unconfirmed
        // create/rotate path — that would silently revoke a live token. Retry
        // the load instead.
        if (isError) {
            void refetch();
            return;
        }
        if (token) {
            setIsConfirmingRotation(true);
            return;
        }
        return rotate();
    }, [isError, refetch, rotate, token]);

    const openWebhookPortal = useCallback(async () => {
        // Open the window synchronously inside the click gesture so popup
        // blockers don't reject it, then point it at the portal URL once the
        // request resolves. `noopener` would make `window.open` return null and
        // cost us the reference, so we sever `opener` manually instead.
        const portalWindow = window.open("about:blank", "_blank");
        if (!portalWindow) {
            // A popup blocker rejected the window, so there is nowhere to send
            // the portal link — bail out before minting one and tell the user.
            toastError(
                "Couldn't open the webhooks portal. Allow popups and try again.",
            );
            return;
        }
        portalWindow.opener = null;
        try {
            const url = await webhookPortalMutation.mutateAsync();
            portalWindow.location.href = url;
        } catch {
            portalWindow.close();
            toastError("Failed to open the webhooks portal");
        }
    }, [toastError, webhookPortalMutation]);

    if (!canManageApi) {
        return null;
    }

    let tokenStatusMessage =
        "No token yet. Create one to start calling the Kick Platform API.";
    if (isLoading) {
        tokenStatusMessage = "Loading…";
    } else if (isError) {
        tokenStatusMessage =
            "Couldn't load the token status. Please try again.";
    }

    let tokenActionLabel = "Create token";
    if (isError) {
        tokenActionLabel = "Retry";
    } else if (token) {
        tokenActionLabel = "Rotate token";
    }

    return (
        <PageSection>
            <div className={styles.groupHeader}>Platform token</div>

            <Card stacked>
                <CardSection>
                    <HStack gap={16} align="center" justify="between">
                        <VStack gap={4}>
                            <header className={styles.sectionHeader}>
                                Token
                            </header>
                            {token ? (
                                <>
                                    <code
                                        className={styles.tokenPrefix}
                                        data-testid="organization-access-token-prefix"
                                    >
                                        {formatTokenPrefix(token.tokenPrefix)}
                                    </code>
                                    <span className={styles.sectionValue}>
                                        Created on{" "}
                                        {formatTokenDate(token.createdAt)} ·
                                        Last used{" "}
                                        {formatTokenDate(token.lastUsedAt)}
                                    </span>
                                </>
                            ) : (
                                <span className={styles.sectionValue}>
                                    {tokenStatusMessage}
                                </span>
                            )}
                        </VStack>
                        <ButtonWithLoader
                            variant="primary"
                            size="md"
                            loading={rotateMutation.isPending}
                            disabled={isLoading}
                            onClick={handleTokenAction}
                            data-testid="organization-access-token-rotate-button"
                        >
                            {tokenActionLabel}
                        </ButtonWithLoader>
                    </HStack>
                </CardSection>
            </Card>

            <div className={styles.groupHeader}>Webhooks</div>

            <Card stacked>
                <CardSection>
                    <HStack gap={16} align="center" justify="between">
                        <VStack gap={4}>
                            <header className={styles.sectionHeader}>
                                Endpoints
                            </header>
                            <span className={styles.sectionValue}>
                                Add endpoints, inspect delivery logs and replay
                                failed events in the webhooks portal.
                            </span>
                        </VStack>
                        <ButtonWithLoader
                            variant="primary"
                            size="md"
                            loading={webhookPortalMutation.isPending}
                            onClick={openWebhookPortal}
                            data-testid="organization-webhook-portal-button"
                        >
                            Open portal
                        </ButtonWithLoader>
                    </HStack>
                </CardSection>
            </Card>

            <ConfirmationModal
                show={isConfirmingRotation}
                onHide={() => setIsConfirmingRotation(false)}
                closeButton
                title="Rotate platform token?"
                question="The current token stops working immediately. Every integration using it has to be updated with the new token."
                onConfirm={rotate}
                onReject={() => setIsConfirmingRotation(false)}
                yes="Rotate"
                yesBtnVariant="destructive"
                no="Cancel"
            />

            {plaintext && (
                <CreatedTokenModal
                    plaintext={plaintext}
                    title="New token created, please copy it now"
                    plaintextTestId="organization-access-token-plaintext"
                    onDismiss={() => setPlaintext(null)}
                />
            )}
        </PageSection>
    );
};
